
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import {
  HiOutlineArrowUpRight,
  HiOutlineCalendarDays,
  HiOutlineBookOpen,
  HiOutlineScale,
} from "react-icons/hi2";

import { media } from "../../server";

/* ============================================
   IMAGE URL HELPER
============================================ */

function getBlogImageUrl(image) {
  if (!image || typeof image !== "string") {
    return null;
  }

  const filename = image.trim();

  if (!filename) return null;

  // Full URL
  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  // Backend media base path
  const base = String(media || "").replace(/\/+$/, "");

  const path = filename.replace(/^\/+/, "");

  return base
    ? `${base}/${path}`
    : `/${path}`;
}

/* ============================================
   SAFE TEXT PREVIEW

   Articles may contain HTML from the editor.
   We display plain text in preview cards.
============================================ */

function getPlainText(value) {
  if (typeof value !== "string") return "";

  const normalized = value.replace(
    /<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi,
    " "
  );

  const sanitized = DOMPurify.sanitize(normalized, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

  if (typeof DOMParser === "undefined") {
    return sanitized.replace(/\s+/g, " ").trim();
  }

  const documentFragment = new DOMParser().parseFromString(
    sanitized,
    "text/html"
  );

  return (documentFragment.body.textContent || "")
    .replace(/\s+/g, " ")
    .trim();
}

/* ============================================
   BLOG IMAGE

   Uses the backend image field.
   Graceful fallback when image is unavailable.
============================================ */

function BlogImage({
  image,
  className = "",
  priority = false,
}) {
  const imageUrl = getBlogImageUrl(image);

  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const showImage = Boolean(imageUrl) && !imageError;

  return (
    <div
      className={`
        relative overflow-hidden
        bg-[#162D40]
        ${className}
      `}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt=""
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onError={() => setImageError(true)}
          className="
            absolute inset-0
            h-full w-full

            object-cover
            object-center

            transition-transform
            duration-700 ease-out

            group-hover:scale-[1.055]
          "
        />
      ) : (
        <div
          className="
            absolute inset-0

            flex items-center justify-center

            bg-gradient-to-br
            from-[#294961]
            via-[#18354A]
            to-[#0B1726]
          "
        >
          {/* Decorative outline */}
          <div
            aria-hidden="true"
            className="
              absolute -end-16 -top-16
              h-56 w-56
              rounded-full
              border border-white/10
            "
          />

          <div
            aria-hidden="true"
            className="
              absolute -start-16 -bottom-20
              h-52 w-52
              rounded-full
              border border-white/10
            "
          />

          <HiOutlineScale
            size={66}
            aria-hidden="true"
            className="
              relative z-10
              text-[#D7B77F]/55
            "
          />
        </div>
      )}
    </div>
  );
}

/* ============================================
   BLOG CARD
============================================ */

function BlogsCard({
  blogs,
  category,
  variant = "default",
  index = 0,
}) {
  const { i18n } = useTranslation();

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const title = isEnglish
    ? blogs?.titleEn
    : blogs?.titleAr;

  const description = useMemo(
    () =>
      getPlainText(
        isEnglish
          ? blogs?.textEn
          : blogs?.textAr
      ),
    [isEnglish, blogs?.textEn, blogs?.textAr]
  );

  const detailPath = `/BlogeDetails/${
    blogs?._id || blogs?.id
  }`;

  const labels = {
    category: isEnglish
      ? "Legal Insights"
      : "رؤى قانونية",

    readMore: isEnglish
      ? "Read Article"
      : "اقرأ المقال",

    featured: isEnglish
      ? "Featured Article"
      : "مقال مميز",
  };

  const articleNumber = String(index + 1).padStart(2, "0");

  /* ======================================
     FEATURED ARTICLE
  ====================================== */

  if (variant === "featured") {
    return (
      <article
        className="
          group relative isolate
          flex min-h-[460px]
          flex-col justify-end

          overflow-hidden
          rounded-[24px]

          border border-white/15
          bg-[#102031]

          shadow-[0_18px_45px_rgba(12,32,50,0.12)]

          sm:min-h-[500px]

          lg:h-full
          lg:min-h-[510px]
        "
      >
        {/* Image */}
        <BlogImage
          image={blogs?.Image}
          priority
          className="absolute inset-0 -z-20"
        />

        {/* Cinematic gradient */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute inset-0 -z-10

            bg-gradient-to-t
            from-[#071522]/95
            via-[#071522]/65
            to-[#071522]/10
          "
        />

        {/* Top badge */}
        <div
          className="
            absolute start-6 top-6
            flex items-center gap-2

            rounded-lg
            border border-white/20

            bg-[#102031]/55
            px-4 py-2

            text-white
            backdrop-blur-lg
          "
        >
          <HiOutlineBookOpen
            size={17}
            aria-hidden="true"
            className="text-[#D7B77F]"
          />

          <span className="caption font-semibold">
            {labels.featured}
          </span>
        </div>

        {/* Article content */}
        <div className="relative p-7 sm:p-9 lg:p-10">

          {/* Date */}
          {blogs?.date && (
            <div className="mb-5 flex items-center gap-2 text-white/75">

              <HiOutlineCalendarDays
                size={18}
                aria-hidden="true"
                className="text-[#D7B77F]"
              />

              <span className="caption">
                {blogs.date}
              </span>

            </div>
          )}

          {/* Title */}
          <Link
            to={detailPath}
            onClick={() => window.scrollTo(0, 0)}
            className="
              block rounded-md
              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-[#D7B77F]
            "
          >
            <h3
              className="
                h3 max-w-[660px]
                line-clamp-2
                text-white

                transition-colors duration-300
                group-hover:text-[#F1E5D1]
              "
            >
              {title}
            </h3>
          </Link>

          {/* Description */}
          <p
            className="
              body-2 mt-5
              max-w-[620px]
              line-clamp-3
              text-white/80
            "
          >
            {description}
          </p>

          {/* Footer */}
          <div
            className="
              mt-8 flex items-center
              justify-between gap-4

              border-t border-white/20
              pt-6
            "
          >
            <Link
              to={detailPath}
              onClick={() => window.scrollTo(0, 0)}
              className="
                group/link inline-flex
                items-center gap-3

                rounded-lg
                text-white

                transition-colors duration-300
                hover:text-[#D7B77F]

                focus-visible:outline-none
                focus-visible:ring-2
                focus-visible:ring-[#D7B77F]
              "
            >
              <span className="button-sm">
                {labels.readMore}
              </span>

              <HiOutlineArrowUpRight
                size={19}
                aria-hidden="true"
                className="directional-arrow"
              />
            </Link>

            <span
              className="
                font-cairo text-lg
                font-semibold
                tabular-nums
                text-[#D7B77F]
              "
            >
              {articleNumber}
            </span>
          </div>

        </div>
      </article>
    );
  }

  /* ======================================
     COMPACT HORIZONTAL ARTICLE
  ====================================== */

  if (variant === "compact") {
    return (
      <article
        className="
          group flex min-h-[220px]
          min-w-0 overflow-hidden

          rounded-[20px]

          border border-[#DCE6ED]
          bg-white

          shadow-[0_10px_35px_rgba(13,35,55,0.045)]

          transition-all duration-300

          hover:-translate-y-1
          hover:border-[#C19B60]/40
          hover:shadow-card

          dark:border-white/10
          dark:bg-[#1C354A]
          dark:shadow-none

          dark:hover:border-[#D7B77F]/30
        "
      >
        {/* Image */}
        <Link
          to={detailPath}
          onClick={() => window.scrollTo(0, 0)}
          aria-label={title}
          className="
            block w-[38%]
            shrink-0 overflow-hidden

            focus-visible:outline-none
            focus-visible:ring-2
            focus-visible:ring-inset
            focus-visible:ring-skin-accent

            sm:w-[40%]
          "
        >
          <BlogImage
            image={blogs?.Image}
            className="h-full min-h-[220px] w-full"
          />
        </Link>

        {/* Text content */}
        <div
          className="
            flex min-w-0 flex-1
            flex-col

            p-4
            sm:p-6
          "
        >
          {/* Date */}
          {blogs?.date && (
            <span className="caption text-skin-muted">
              {blogs.date}
            </span>
          )}

          {/* Title */}
          <Link
            to={detailPath}
            onClick={() => window.scrollTo(0, 0)}
            className="
              mt-3 block
              rounded-md

              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-skin-accent
            "
          >
            <h3
              className="
                font-cairo
                text-base font-semibold
                leading-[1.8]

                line-clamp-2
                text-skin-heading

                transition-colors duration-300

                group-hover:text-skin-accent
                sm:text-lg
              "
            >
              {title}
            </h3>
          </Link>

          {/* Description */}
          <p
            className="
              body-3 mt-3
              line-clamp-2
              text-skin-muted
            "
          >
            {description}
          </p>

          {/* Action */}
          <Link
            to={detailPath}
            onClick={() => window.scrollTo(0, 0)}
            className="
              mt-auto inline-flex
              items-center gap-2
              self-start rounded-md
              pt-4

              text-skin-accent

              transition-colors
              hover:text-skin-heading

              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-skin-accent
            "
          >
            <span className="button-sm">
              {labels.readMore}
            </span>

            <HiOutlineArrowUpRight
              size={17}
              aria-hidden="true"
              className="directional-arrow"
            />
          </Link>

        </div>
      </article>
    );
  }

  /* ======================================
     DEFAULT ARTICLE CARD
  ====================================== */

  return (
    <article
      className="
        group flex h-full
        min-w-0 flex-col
        overflow-hidden

        rounded-[22px]

        border border-[#DCE6ED]
        bg-white

        shadow-[0_10px_35px_rgba(13,35,55,0.045)]

        transition-all duration-300

        hover:-translate-y-1.5
        hover:border-[#C19B60]/40
        hover:shadow-card

        dark:border-white/10
        dark:bg-[#1C354A]
        dark:shadow-none

        dark:hover:border-[#D7B77F]/30
      "
    >
      {/* ==================================
          IMAGE
      ================================== */}

      <Link
        to={detailPath}
        onClick={() => window.scrollTo(0, 0)}
        aria-label={title}
        className="
          relative block
          aspect-[16/10]
          w-full overflow-hidden

          focus-visible:outline-none
          focus-visible:ring-2
          focus-visible:ring-inset
          focus-visible:ring-skin-accent
        "
      >
        <BlogImage
          image={blogs?.Image}
          className="h-full w-full"
        />

        {/* Image number */}
        <span
          className="
            absolute end-4 top-4

            rounded-lg
            border border-white/20
            bg-[#102031]/60

            px-3 py-1.5

            font-cairo text-xs
            font-semibold
            tabular-nums

            text-white
            backdrop-blur-lg
          "
        >
          {articleNumber}
        </span>
      </Link>

      {/* ==================================
          TEXT CONTENT
      ================================== */}

      <div
        className="
          flex flex-1
          flex-col

          px-6 pb-6 pt-6
          sm:px-7 sm:pb-7
        "
      >
        {/* Category label */}
        <div className="mb-4 flex items-center gap-3">

          <span
            aria-hidden="true"
            className="h-px w-7 bg-skin-accent"
          />

          <span className="eyebrow text-skin-accent">
            {labels.category}
          </span>

        </div>

        {/* Date */}
        {blogs?.date && (
          <div className="mb-3 flex items-center gap-2">

            <HiOutlineCalendarDays
              size={16}
              aria-hidden="true"
              className="text-skin-muted"
            />

            <span className="caption text-skin-muted">
              {blogs.date}
            </span>

          </div>
        )}

        {/* Title */}
        <Link
          to={detailPath}
          onClick={() => window.scrollTo(0, 0)}
          className="
            block rounded-md

            focus-visible:outline-none
            focus-visible:ring-2
            focus-visible:ring-skin-accent
          "
        >
          <h3
            className="
              card-title
              line-clamp-2
              text-skin-heading

              transition-colors duration-300

              group-hover:text-skin-accent
            "
          >
            {title}
          </h3>
        </Link>

        {/* Description */}
        <p
          className="
            card-description
            mt-4
            line-clamp-3
            text-skin-muted
          "
        >
          {description}
        </p>

        {/* ==================================
            FOOTER
        ================================== */}

        <div className="mt-auto pt-8">

          <div className="mb-5 h-px bg-skin-border" />

          <Link
            to={detailPath}
            onClick={() => window.scrollTo(0, 0)}
            className="
              group/link flex
              items-center justify-between
              gap-4 rounded-lg

              text-skin-heading

              transition-colors duration-300

              hover:text-skin-accent

              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-skin-accent
            "
          >
            <span className="button-sm">
              {labels.readMore}
            </span>

            <span
              className="
                flex h-10 w-10
                items-center justify-center

                rounded-xl

                border border-skin-border
                bg-skin-soft

                text-skin-heading

                transition-all duration-300

                group-hover/link:border-brand-navy
                group-hover/link:bg-brand-navy
                group-hover/link:text-white

                dark:group-hover/link:border-white/20
                dark:group-hover/link:bg-white/15
              "
            >
              <HiOutlineArrowUpRight
                size={19}
                aria-hidden="true"
                className="directional-arrow"
              />
            </span>

          </Link>

        </div>
      </div>
    </article>
  );
}

export default BlogsCard;
